import { useEffect, useRef } from 'react'
import css from './EditableText.module.css'

interface EditableTextProps {
    isEditing: boolean
    text: string
    onChange: (value: string) => void
    onBlur: () => void
    onStartEditing?: () => void
    clickable?: boolean
    wrap?: boolean
}

const EditableText = ({
                          isEditing,
                          text,
                          onChange,
                          onBlur,
                          onStartEditing,
                          clickable = false,
                          wrap = false,
                      }: EditableTextProps) => {
    const ref = useRef<HTMLSpanElement>(null)
    const clickPositionRef = useRef<{ x: number, y: number } | null>(null)

    useEffect(() => {
        if (ref.current && ref.current.textContent !== text) {
            const sel = window.getSelection()
            let cursorOffset = 0

            if (isEditing && sel && sel.rangeCount > 0 && ref.current.contains(sel.anchorNode)) {
                const range = sel.getRangeAt(0)
                cursorOffset = range.startOffset
            }

            ref.current.textContent = text

            if (isEditing && ref.current.firstChild) {
                const range = document.createRange()
                const newOffset = Math.min(cursorOffset, ref.current.textContent?.length || 0)
                range.setStart(ref.current.firstChild, newOffset)
                range.collapse(true)

                sel?.removeAllRanges()
                sel?.addRange(range)
            }
        }
    }, [text, isEditing])

    useEffect(() => {
        if (isEditing && ref.current) {
            const wasAlreadyFocused = document.activeElement === ref.current

            // Safari fix: ensure there's a text node with content for cursor placement
            if (!ref.current.firstChild || ref.current.textContent === '') {
                ref.current.textContent = '\u200B'
                ref.current.focus()
                const sel = window.getSelection()
                const range = document.createRange()
                range.setStart(ref.current.firstChild!, 1)
                range.collapse(true)
                sel?.removeAllRanges()
                sel?.addRange(range)
                return
            }

            if (!wasAlreadyFocused) {
                ref.current.focus()

                if (clickPositionRef.current) {
                    const { x, y } = clickPositionRef.current
                    const range = document.caretRangeFromPoint?.(x, y)
                    if (range) {
                        const sel = window.getSelection()
                        sel?.removeAllRanges()
                        sel?.addRange(range)
                    }
                    clickPositionRef.current = null
                } else {
                    const sel = window.getSelection()
                    const range = document.createRange()

                    if (ref.current.firstChild) {
                        range.selectNodeContents(ref.current)
                        range.collapse(false)
                    } else {
                        range.setStart(ref.current, 0)
                        range.collapse(true)
                    }

                    sel?.removeAllRanges()
                    sel?.addRange(range)
                }
            }
        }
    }, [isEditing])

    const handleClick = (e: React.MouseEvent) => {
        if (!isEditing && onStartEditing) {
            // Don't enter edit on the click that ends a text selection (drag-select to copy) —
            // it would collapse the selection. A plain click leaves it collapsed and edits (SCD-896).
            const selection = window.getSelection()
            if (selection && !selection.isCollapsed && selection.toString().trim() !== '') return
            e.stopPropagation()
            clickPositionRef.current = { x: e.clientX, y: e.clientY }
            onStartEditing()
        } else if (isEditing) {
            e.stopPropagation()
        }
    }

    const className = isEditing
        ? css.editable
        : onStartEditing
            ? css.non_editable
            : clickable
                ? css.clickable
                : ''

    return (
        <span
            ref={ref}
            contentEditable={isEditing}
            suppressContentEditableWarning
            className={className}
            style={{ whiteSpace: wrap ? 'normal' : 'nowrap' }}
            onInput={(e) => {
                if (isEditing) {
                    // Remove zero-width space used for Safari fix
                    const content = (e.currentTarget.textContent ?? '').replace(/\u200B/g, '')
                    onChange(content)
                }
            }}
            onBlur={() => {
                if (isEditing) {
                    onBlur()
                }
            }}
            onKeyDown={(e) => {
                if (!isEditing) return

                if (e.key === 'Enter') {
                    e.preventDefault()
                    ref.current?.blur()
                    return
                }

                if (e.key === 'Escape') {
                    e.preventDefault()
                    ref.current!.textContent = text
                    ref.current?.blur()
                    return
                }
            }}
            onPaste={(e) => {
                e.preventDefault()
                const plainText = e.clipboardData.getData('text/plain')
                const sel = window.getSelection()
                if (sel && sel.rangeCount > 0) {
                    const range = sel.getRangeAt(0)
                    range.deleteContents()
                    range.insertNode(document.createTextNode(plainText))
                    range.collapse(false)
                    sel.removeAllRanges()
                    sel.addRange(range)
                    onChange(ref.current?.textContent ?? '')
                }
            }}
            onClick={handleClick}
        />
    )
}

export default EditableText
